import { DreamBlock } from '@typedream/core';
import { findNodePath, toDOMNode } from '@udecode/plate-core';
import { useEffect, useRef } from 'react';
import { useDragLayer } from 'react-dnd';
import { Floating } from '../../../components/Floating';
import { getBlockOptions, isBlockEmpty, isPathRoot } from '../../../editorTools/queries';
import { useTPlateEditorState } from '../../../plate';
import { DragHandle } from '../../dnd/components/DragHandle';
import AddAccordionItemButton from '../../../nodes/accordion/components/AddAccordionItemButton';
import { resizeSelectedElement } from '../transforms';
import { Placeholder } from './Placeholder';
import { ResizeHandles } from './ResizeHandles';

interface ElementBoxProps {
  element: DreamBlock
  isSelected?: boolean
  isHovered?: boolean
  referenceScale?: number
}

export function ElementBox(props: ElementBoxProps) {
  const {
    element, isSelected, isHovered, referenceScale,
  } = props;

  const editor = useTPlateEditorState();

  const domElement = toDOMNode(editor, element);

  const { isResizable, isDraggable, format } = getBlockOptions(editor, element) || {};

  const { placeholderText } = format || {};

  const isEmpty = isBlockEmpty(editor, element);

  const path = findNodePath(editor, element);

  const floatingRef = useRef<Floating>(null);

  useEffect(() => {
    const floatingUpdate = () => {
      floatingRef.current?.update();
    };

    // update the floating ref every 100ms regardless of whether there's a change
    // this is to prevent the floating ref from getting out of sync with the dom
    // element
    const interval = setInterval(() => {
      floatingUpdate();
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const { isDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));

  if (!domElement) return null;

  return (
    <Floating
      ref={floatingRef}
      reference={domElement}
      referenceScale={referenceScale}
      cover
      autoUpdate
      style={{
        ...(isDragging ? { visibility: 'hidden' } : {}),
        outline: `${
          isSelected ? 2 : 1
        }px solid var(--background-highlight-highlight)`,
      }}
    >
      {isHovered && isDraggable && <DragHandle element={element} />}
      {isSelected && isResizable && (
        <ResizeHandles
          element={element}
          onResize={(sizes) => resizeSelectedElement(editor, sizes)}
        />
      )}
      {isEmpty && (
        <Placeholder element={element} isRoot={isPathRoot(path || [])}>
          {placeholderText}
        </Placeholder>
      )}
      {(element.type === 'accordion_item' || element.type === 'accordion')
       && isSelected && (
       <AddAccordionItemButton editor={editor} element={element} />
      )}
    </Floating>
  );
}

ElementBox.defaultProps = {
  isSelected: false,
  isHovered: false,
  referenceScale: 1,
};
